import { useState, useEffect, useCallback } from "react";
import { API, useAuth , authFetch } from "@/App";
import { IntelligenceSnapshot } from "@/components/IntelligenceSnapshot";
import {
  ShoppingCart,
  Wallet,
  Users,
  AlertCircle,
  Store,
  Package,
  ClipboardList,
  TrendingUp,
  ArrowRight,
  ScrollText,
  Shield,
  Target,
  Clock,
  MapPin,
  Globe2,
  ChevronRight,
  FileCheck,
  FileText,
  Award,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link, useSearchParams } from "react-router-dom";

const REGION_CONFIG = {
  ALL: { label: "All India", icon: Globe2, color: "#1A1A2E", bg: "bg-[#1A1A2E]", text: "text-white", accent: "#1A1A2E" },
  GJ: { label: "Gujarat", icon: MapPin, color: "#C0272D", bg: "bg-[#C0272D]", text: "text-white", accent: "#C0272D" },
  RJ: { label: "Rajasthan", icon: MapPin, color: "#F5A623", bg: "bg-[#F5A623]", text: "text-white", accent: "#F5A623" },
};

const formatINR = (paise) => {
  if (!paise) return "0";
  const rupees = paise / 100;
  if (rupees >= 10000000) return `${(rupees / 10000000).toFixed(1)}Cr`;
  if (rupees >= 100000) return `${(rupees / 100000).toFixed(1)}L`;
  if (rupees >= 1000) return `${(rupees / 1000).toFixed(1)}K`;
  return rupees.toFixed(0);
};

const RegionSwitcher = ({ active, onChange, isSuperAdmin }) => {
  if (!isSuperAdmin) return null;
  const regions = ["ALL", "GJ", "RJ"];
  return (
    <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl" data-testid="region-switcher">
      {regions.map((r) => {
        const cfg = REGION_CONFIG[r];
        const isActive = active === r;
        return (
          <button
            key={r}
            onClick={() => onChange(r)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              isActive ? `${cfg.bg} ${cfg.text} shadow-md` : "text-slate-500 hover:text-slate-700 hover:bg-white"
            }`}
            data-testid={`region-btn-${r}`}
          >
            <cfg.icon className="w-3.5 h-3.5" />
            {cfg.label}
          </button>
        );
      })}
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [searchParams, setSearchParams] = useSearchParams();
  
  const userRegion = user?.region_id || "ALL";
  const defaultRegion = isSuperAdmin
    ? (localStorage.getItem("pravaah_region") || searchParams.get("region") || "ALL")
    : userRegion;
  
  const [activeRegion, setActiveRegion] = useState(defaultRegion);
  const [stats, setStats] = useState(null);
  const [regional, setRegional] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsights, setAiInsights] = useState(null);
  const [onboarding, setOnboarding] = useState(null);

  const handleRegionChange = useCallback((r) => {
    setActiveRegion(r);
    setSearchParams({ region: r });
    localStorage.setItem("pravaah_region", r);
  }, [setSearchParams]);

  useEffect(() => {
    fetchData();
  }, [activeRegion]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const regionParam = activeRegion !== "ALL" ? `?region=${activeRegion}` : "";
      const promises = [
        authFetch(`${API}/dashboard/stats${regionParam}`, {  }).then((r) => r.json()),
        authFetch(`${API}/dashboard/regional?region=${activeRegion}`, {  }).then((r) => r.json()),
      ];
      if (isSuperAdmin) {
        promises.push(authFetch(`${API}/dashboard/admin-insights`, {  }).then((r) => r.json()));
      }
      const results = await Promise.all(promises);
      if (results[0].success) setStats(results[0].data);
      if (results[1].success) setRegional(results[1].data);
      if (results[2]?.success) setInsights(results[2].data);
      
      // Fetch AI Business Insights
      try {
        const aiRes = await authFetch(`${API}/ai/insights`);
        const aiData = await aiRes.json();
        if (aiData.success) setAiInsights(aiData.data);
      } catch {}
      // Fetch onboarding tracker
      try {
        const obRes = await authFetch(`${API}/dashboard/onboarding-tracker`);
        const obData = await obRes.json();
        if (obData.success) setOnboarding(obData.data);
      } catch {}
    } catch (error) {
      console.error("Failed to fetch:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const diff = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const regionCfg = REGION_CONFIG[activeRegion] || REGION_CONFIG.ALL;

  if (loading && !stats) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-slate-100 rounded-xl animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (<div key={i} className="h-28 bg-slate-100 rounded-xl animate-pulse" />))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (<div key={i} className="h-48 bg-slate-100 rounded-xl animate-pulse" />))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Region Switcher Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-2 h-8 rounded-full" style={{ backgroundColor: regionCfg.accent }} />
          <div>
            <h1 className="text-xl font-bold text-slate-900 font-['Sora']">
              {!isSuperAdmin ? `${REGION_CONFIG[userRegion]?.label || "Regional"} Operations` : regionCfg.label === "All India" ? "All India Overview" : `${regionCfg.label} Operations`}
            </h1>
            <p className="text-sm text-slate-500">
              {user?.name} &middot; {user?.role?.replace("_", " ")}
            </p>
          </div>
        </div>
        <RegionSwitcher active={activeRegion} onChange={handleRegionChange} isSuperAdmin={isSuperAdmin} />
      </div>

      {/* Intelligence Snapshot — combined overview of targets, leaderboard, top product/month, receivables */}
      <IntelligenceSnapshot />

      {/* KPI Row 1 — Business Pulse */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Orders Today", value: stats?.orders_today || 0, sub: `${formatINR(stats?.orders_value_today)}`, icon: ShoppingCart, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Collections Today", value: formatINR(stats?.collections_today), icon: Wallet, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Active Staff", value: `${stats?.active_employees || 0}/${stats?.total_employees || 0}`, icon: Users, color: "text-violet-600", bg: "bg-violet-50" },
          { label: "Pending Approvals", value: stats?.pending_approvals || 0, icon: AlertCircle, color: "text-amber-600", bg: "bg-amber-50" },
        ].map((kpi, i) => (
          <Card key={i} className="p-4" data-testid={`kpi-${i}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">{kpi.label}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1 font-mono">{kpi.value}</p>
                {kpi.sub && <p className="text-xs text-slate-500 mt-0.5">{kpi.sub}</p>}
              </div>
              <div className={`p-2 rounded-lg ${kpi.bg}`}>
                <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* KPI Row 2 — Regional Comparison Cards (All India view) */}
      {activeRegion === "ALL" && regional?.region_stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="region-comparison">
          {["GJ", "RJ"].map((rc) => {
            const rs = regional.region_stats[rc];
            const cfg = REGION_CONFIG[rc];
            if (!rs) return null;
            return (
              <Card key={rc} className="p-5 border-l-4" style={{ borderLeftColor: cfg.color }} data-testid={`region-card-${rc}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cfg.color }} />
                    <h3 className="font-semibold text-slate-900 text-sm">{cfg.label}</h3>
                  </div>
                  <button
                    onClick={() => handleRegionChange(rc)}
                    className="text-xs font-medium flex items-center gap-1 hover:underline"
                    style={{ color: cfg.color }}
                    data-testid={`view-region-${rc}`}
                  >
                    View {cfg.label} <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-400">Orders Today</p>
                    <p className="text-lg font-bold font-mono text-slate-900">{rs.orders_today}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Revenue (MTD)</p>
                    <p className="text-lg font-bold font-mono text-slate-900">{formatINR(rs.revenue_mtd)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Dealers</p>
                    <p className="text-lg font-bold font-mono text-slate-900">{rs.total_dealers}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Target</p>
                    <p className="text-lg font-bold font-mono" style={{ color: cfg.color }}>{rs.target_pct}%</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Regional specific: Top Performers */}
      {activeRegion !== "ALL" && regional?.top_performers?.length > 0 && (
        <Card className="p-5" data-testid="top-performers">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4" style={{ color: regionCfg.accent }} />
            <h3 className="font-semibold text-slate-900 text-sm">Top Performers — {regionCfg.label}</h3>
          </div>
          <div className="space-y-2">
            {regional.top_performers.map((p, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: regionCfg.accent }}>
                    {i + 1}
                  </div>
                  <span className="text-sm font-medium text-slate-900">{p.name}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold font-mono text-slate-900">{formatINR(p.revenue)}</p>
                  <p className="text-[10px] text-slate-400">{p.orders} orders</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* District Performance Table (Region-specific view) */}
      {activeRegion !== "ALL" && regional?.district_stats?.length > 0 && (
        <Card className="p-5" data-testid="district-table">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4" style={{ color: regionCfg.accent }} />
            <h3 className="font-semibold text-slate-900 text-sm">District Performance — {regionCfg.label}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th>District</th>
                  <th>Dealers</th>
                  <th>Orders (MTD)</th>
                  <th>Revenue</th>
                  <th>Collections</th>
                  <th>Staff</th>
                </tr>
              </thead>
              <tbody>
                {regional.district_stats.map((d) => (
                  <tr key={d.district}>
                    <td className="font-medium">{d.district}</td>
                    <td className="font-mono">{d.dealers}</td>
                    <td className="font-mono">{d.orders_mtd}</td>
                    <td className="font-mono">{formatINR(d.revenue_mtd)}</td>
                    <td className="font-mono">{formatINR(d.collections_mtd)}</td>
                    <td className="font-mono">{d.employees}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Business Intelligence Panel */}
      {aiInsights && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="p-6" data-testid="party-type-chart">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Store className="w-4 h-4 text-[#8B1A1A]" /> Dealer Type Distribution
            </h3>
            <div className="space-y-3">
              {Object.entries(aiInsights.party_type_distribution || {}).map(([type, count]) => {
                const total = Object.values(aiInsights.party_type_distribution).reduce((a, b) => a + b, 0);
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                const colors = { DEALER: "#3B82F6", CONTRACTOR: "#10B981", END_USER: "#6B7280", BUILDER: "#8B5CF6", DISTRIBUTOR: "#F59E0B" };
                return (
                  <div key={type} className="flex items-center gap-3">
                    <div className="w-20 text-xs font-medium text-slate-600">{type}</div>
                    <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-1000 flex items-center px-2"
                        style={{ width: `${Math.max(pct, 5)}%`, backgroundColor: colors[type] || "#6B7280" }}>
                        <span className="text-[10px] text-white font-bold">{count}</span>
                      </div>
                    </div>
                    <span className="text-xs font-mono text-slate-500 w-10 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-6" data-testid="top-districts-chart">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#8B1A1A]" /> Top Districts by Dealers
            </h3>
            <div className="space-y-2">
              {(aiInsights.top_districts || []).slice(0, 8).map((d, i) => {
                const max = (aiInsights.top_districts[0]?.dealers || 1);
                const pct = Math.round((d.dealers / max) * 100);
                return (
                  <div key={d.district} className="flex items-center gap-3">
                    <span className="w-4 text-xs font-bold text-slate-400 font-mono">{i + 1}</span>
                    <div className="w-24 text-xs font-medium text-slate-700 truncate">{d.district || "Unknown"}</div>
                    <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#8B1A1A] to-[#8B1A1A]/60 rounded transition-all duration-700"
                        style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-bold font-mono text-slate-900 w-8 text-right">{d.dealers}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* Recent Activity & Sidebar Widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Audit Activity */}
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ScrollText className="w-4 h-4 text-slate-400" />
              <h3 className="font-semibold text-slate-900">Recent Activity</h3>
            </div>
            <Link to="/audit" className="text-sm text-[#8B1A1A] hover:underline" data-testid="view-audit-link">
              View all
            </Link>
          </div>
          <div className="space-y-2" data-testid="recent-audit-feed">
            {insights?.recent_audit_logs?.length > 0 ? (
              insights.recent_audit_logs.map((log, i) => {
                const actionColor = log.action === "CREATE" ? "bg-emerald-100 text-emerald-700" : log.action === "DELETE" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700";
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`audit-feed-item-${i}`}>
                    <div className="w-8 h-8 rounded-full bg-[#1A1A2E] flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                      {(log.user_name || "?").charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 flex items-center gap-1 flex-wrap">
                        <span className="font-medium">{log.user_name || "System"}</span>
                        <Badge className={`text-[10px] px-1.5 py-0 ${actionColor}`}>{log.action}</Badge>
                        <span className="text-slate-500">{log.module}</span>
                        {log.entity_name && <span className="text-slate-400">&middot; {log.entity_name}</span>}
                      </div>
                    </div>
                    <span className="text-[11px] text-slate-400 whitespace-nowrap flex items-center gap-1">
                      <Clock className="w-3 h-3" />{getTimeAgo(log.timestamp)}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6">
                <ScrollText className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                <p className="text-sm text-slate-400">No activity yet. Changes will appear here.</p>
              </div>
            )}
          </div>
        </Card>

        {/* Right Sidebar */}
        <div className="space-y-4">
          {/* Access Overview */}
          {isSuperAdmin && insights?.permissions && (
            <Card className="p-5" data-testid="permissions-widget">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-4 h-4 text-[#8B1A1A]" />
                <h3 className="font-semibold text-slate-900 text-sm">Access Overview</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Active Users</span>
                  <span className="font-bold text-slate-900 font-mono">{insights.permissions.total_active_users}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Super Admins</span>
                  <span className="font-bold text-[#8B1A1A] font-mono">{insights.permissions.super_admins}</span>
                </div>
                <Link to="/users" className="flex items-center gap-1 text-xs text-[#8B1A1A] hover:underline pt-1" data-testid="manage-access-link">
                  Manage access <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </Card>
          )}

          {/* Targets */}
          {insights?.targets_summary && (
            <Card className="p-5" data-testid="targets-widget">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-emerald-600" />
                <h3 className="font-semibold text-slate-900 text-sm">Targets</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Active</span>
                  <span className="font-bold text-amber-600 font-mono">{insights.targets_summary.active}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Completed</span>
                  <span className="font-bold text-emerald-600 font-mono">{insights.targets_summary.completed}</span>
                </div>
                <Link to="/targets" className="flex items-center gap-1 text-xs text-[#8B1A1A] hover:underline pt-1" data-testid="view-targets-link">
                  View all targets <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </Card>
          )}

          {/* Quick Actions */}
          <Card className="p-5">
            <h3 className="font-semibold text-slate-900 text-sm mb-3">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { to: "/orders", icon: ShoppingCart, label: "New Order" },
                { to: "/dealers", icon: Store, label: "Add Dealer" },
                { to: "/collections", icon: Wallet, label: "Collection" },
                { to: "/tasks", icon: ClipboardList, label: "Assign Task" },
                { to: "/sales-analytics", icon: TrendingUp, label: "Sales Analytics", highlight: true },
                { to: "/scheme-claims", icon: Award, label: "Scheme Claims" },
              ].map((a) => (
                <Link key={a.to} to={a.to} data-testid={`quick-action-${a.to.replace("/", "")}`}>
                  <div className={`p-3 rounded-lg border transition-colors text-center cursor-pointer ${a.highlight ? "border-[#8B1A1A] bg-[#8B1A1A]/5 hover:bg-[#8B1A1A]/10" : "border-slate-200 hover:border-[#8B1A1A] hover:bg-[#8B1A1A]/5"}`}>
                    <a.icon className="w-4 h-4 text-[#8B1A1A] mx-auto mb-1" />
                    <p className="text-xs font-medium text-slate-700">{a.label}</p>
                  </div>
                </Link>
              ))}
            </div>
          </Card>

          {/* Onboarding Progress Tracker */}
          {onboarding && (
            <Card className="p-5" data-testid="onboarding-tracker">
              <div className="flex items-center gap-2 mb-4">
                <FileCheck className="w-4 h-4 text-[#8B1A1A]" />
                <h3 className="font-semibold text-slate-900 text-sm">Dealer Onboarding</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Total Dealers</span>
                  <span className="font-bold text-slate-900 font-mono">{onboarding.total}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Full BA Forms</span>
                  <span className="font-bold text-emerald-600 font-mono">{onboarding.full_registrations}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Quick (Incomplete)</span>
                  <span className="font-bold text-amber-600 font-mono">{onboarding.quick_registrations}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Pending Approval</span>
                  <span className="font-bold text-red-500 font-mono">{onboarding.pending_approval}</span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-400">Completion</span>
                    <span className="font-bold" style={{ color: onboarding.completion_pct >= 80 ? "#10B981" : "#F59E0B" }}>{onboarding.completion_pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full">
                    <div className="h-full rounded-full transition-all" style={{ width: `${onboarding.completion_pct}%`, backgroundColor: onboarding.completion_pct >= 80 ? "#10B981" : "#F59E0B" }} />
                  </div>
                </div>
                <Link to="/dealers" className="flex items-center gap-1 text-xs text-[#8B1A1A] hover:underline pt-1" data-testid="view-incomplete-link">
                  View incomplete dealers <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
